import type { Branding, Survey } from './types.ts';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new ApiError(res.status, payload.message ?? payload.error ?? res.statusText, payload.error);
  }
  if (res.headers.get('content-type')?.includes('application/json')) return (await res.json()) as T;
  return undefined as T;
}

// Every authenticated call names the engagement slug it's for (?slug=...) — the session
// cookie itself isn't scoped per-engagement, so the backend cross-checks this against the
// resolved session and 401s on a mismatch, rather than silently resuming a stale session
// from a different engagement tested earlier in the same browser. See routes/survey.ts.
export const api = {
  branding: (slug: string) => request<Branding>('GET', `/api/survey/${slug}/branding`),
  login: (slug: string, username: string, password: string) =>
    request<{ ok: true }>('POST', `/api/survey/${slug}/login`, { username, password }),
  survey: (slug: string) => request<Survey>('GET', `/api/survey/questions?slug=${encodeURIComponent(slug)}`),
  // value = 1..5, or null to skip / clear the answer.
  saveAnswer: (slug: string, ref: number, value: number | null, comment?: string) =>
    request<{ ok: true }>('PUT', `/api/survey/answers/${ref}?slug=${encodeURIComponent(slug)}`, {
      value,
      ...(comment !== undefined && { comment }),
    }),
  submit: (slug: string) => request<{ ok: true }>('POST', `/api/survey/submit?slug=${encodeURIComponent(slug)}`),
  logout: (slug: string) => request<{ ok: true }>('POST', `/api/survey/logout?slug=${encodeURIComponent(slug)}`),
  pdfUrl: (slug: string) => `/api/survey/export.pdf?slug=${encodeURIComponent(slug)}`,
};
